import { useState, useEffect, useCallback } from 'react'

import { showLoading } from 'services/util'
import AuctionImageDropzone from '../components/AuctionImageDropzone'

import Alert from 'common/notifications/Alert'
import iconInfo from 'images/iconColorinfo.svg'
import success from 'images/TickMark.svg'
import * as colors from 'css/Colors'
import css from './sass/upload.documents.module.scss'
import themeCss from '../components/sass/theme.module.scss'

import { adminGetAuctionImagePresignedUrl } from "./auctionsadminutil"
import { updateAuctionItemMutation, updateAuctionMutation, updateFixedItemMutation } from '../graphql/Mutations'
import { useMutation } from '@tanstack/react-query'
import { graphql } from '../api'
import { setSessionItem } from '../components/utils'

import ImageEditor from './ImageEditor'

const successPrefix = "All files uploaded successfully:"
const emptyFn = () => {}

function UploadFiles (props) {
  const [selectedFiles, setSelectedFiles] = useState([])
  const [dropzoneKey, setDropzoneKey] = useState(0)
  const [submitError, setSubmitError] = useState('')
  const [submitSuccess, setSubmitSuccess] = useState('')
  const [displayThumbnail, setDisplayThumbnail] = useState()

  // editor variables
  const [isEditing, setIsEditing] = useState(false) 
  const [workingFile, setWorkingFile] = useState(null)

  const auctionId = props.auction?.AuctionId
  const auctionCode = props.auction?.AuctionCode
  const itemId = props.item?.ItemId
  const fixedItemId = props.item?.FixedItemId
  const itemCode = props.item?.ItemCode
  const refreshQuery = props.refreshQuery
  const isReadOnly = props.isReadOnly
  const setIsImageSelected = props.setIsImageSelected || emptyFn
  const [uploadDone, setUploadDone] = useState(false)

  const validateFilenames = useCallback((files, docType) => {
    const errors = []
    const file = files[0]

    validateImageFile(file, errors)

    return (errors.length === 0) ? '' : (
      <>
        <p>Filetype is not allowed. Please select a file that is *.png, *.jpeg, *.jpg, or *.avif and try again:</p>
        <ul>
          {errors.map((error, index) => (
            <li key={index}><b>{error.msg}: {error.file.name}</b></li>
          ))}
        </ul>
      </>
    )
  }, [])
  
  useEffect(() => {
    if (selectedFiles.length === 0) {
      setSubmitError('')
      setIsImageSelected(false)
    }
    else if (selectedFiles.length > 0) {
      setSubmitError(validateFilenames(selectedFiles))
      setSubmitSuccess('')
      setIsImageSelected(true)
    }
  }, [selectedFiles, validateFilenames, setIsImageSelected])

  useEffect(() => {
    setSubmitError('')
    setSubmitSuccess('')
  }, [])

  useEffect(() => {
    if (selectedFiles.length > 0) {
      setWorkingFile(selectedFiles[0])
      setIsEditing(true)
    }
    if (selectedFiles.length === 0 && uploadDone) {
      setIsEditing(false)
      setWorkingFile(null)
    }
    
  }, [selectedFiles, uploadDone])

  //updates to database after image uploads
  const {data: updateAuctionResponse, updateAuctionError, updateAuctionPending, mutate: saveAuction } = useMutation({
    mutationFn: (input) => {
      return graphql({query: updateAuctionMutation, variables: {input: input} })
    },
  })

  //updates to database after image uploads
  const {data: updateAuctionItemResponse, updateAuctionItemError, updateAuctionItemPending, mutate: saveItem } = useMutation({
    mutationFn: (input) => {
      return graphql({query: updateAuctionItemMutation, variables: {input: input} })
    },
  })

  const {data: updateFixedItemResponse, updateFixedItemError, updateFixedItemPending, mutate: saveFixedItem } = useMutation({
    mutationFn: (input) => {
      return graphql({query: updateFixedItemMutation, variables: {input: input} })
    },
  })

  const auctionUpdate = async () => {
    const input = {
        AuctionId: auctionId,
        BannerImageName: selectedFiles[0]?.path,
      }
      saveAuction(input)
  }

  //use effect for auction updating error
  useEffect(() => {

    if (updateAuctionError) {
      console.log("Auction not created: ", updateAuctionError)
      console.log(updateAuctionError.errors[0].message) // errors from Appsync take this format, there may be other formats depending on where the operation fails
      // Function to clear image upload flag on error
      setSessionItem('imageUploadInProgress', null)
    }
    else if (updateAuctionResponse) {
      console.log(updateAuctionResponse)
      console.log('Auction updated: ', updateAuctionResponse.data.updateAuction.AuctionId)
      refreshQuery()
      // Note: Don't clear imageUploadInProgress here - let AuctionEdit component handle it after full refresh
    }
  }, [updateAuctionResponse, updateAuctionError, updateAuctionPending, refreshQuery])

  const itemUpdate = async () => {
    const input = { ImageFilename: selectedFiles[0]?.path }

      if (itemId) {
        input['ItemId'] = itemId
        saveItem(input)
      }
      if (fixedItemId) {
        input['FixedItemId'] = fixedItemId
        saveFixedItem(input)
      }

      refreshQuery()
  }

  //use effect for fixed Item updating error
  useEffect(() => {

    if (updateFixedItemError) {
      console.log("Auction not created: ", updateFixedItemError)
      console.log(updateFixedItemError.errors[0].message) // errors from Appsync take this format, there may be other formats depending on where the operation fails
      // Function to clear image upload flag on error
      setSessionItem('imageUploadInProgress', null)
    }
    else if (updateFixedItemResponse) {
      console.log(updateFixedItemResponse)
      console.log('Fixed Item updated: ', updateFixedItemResponse.data.updateFixedItem.FixedItemId)
      refreshQuery()
      // Function to clear image upload flag after successful item update
      sessionStorage.removeItem('imageUploadInProgress')
    }
  }, [updateFixedItemResponse, updateFixedItemError, updateFixedItemPending, refreshQuery])

    //use effect for auction Item updating error
  useEffect(() => {

    if (updateAuctionItemError) {
      console.log("Auction not created: ", updateAuctionItemError)
      console.log(updateAuctionItemError.errors[0].message) // errors from Appsync take this format, there may be other formats depending on where the operation fails
      // Function to clear image upload flag on error
      setSessionItem('imageUploadInProgress', null)
    }
    else if (updateAuctionItemResponse) {
      console.log(updateAuctionItemResponse)
      console.log('Item updated: ', updateAuctionItemResponse.data.updateAuctionItem.ItemId)
      refreshQuery()
      // Function to clear image upload flag after successful item update
      sessionStorage.removeItem('imageUploadInProgress')
    }
  }, [updateAuctionItemResponse, updateAuctionItemError, updateAuctionItemPending, refreshQuery])

  const validateImageFile = (file, errors) => {
    console.log({file, errors})
    const accepted = ['image/jpeg', 'image/png', 'image/avif']

    if (accepted.indexOf(file.type) === -1) {
      errors.push({file, msg: "Filename has too many sections"})
    }
  }

  const doSubmitSuccess = (files) => (
    <>
      <b>{successPrefix}</b>
      <ul>
        {files.map((file, index) => (<li key={index}>{file.name}</li>))}
      </ul>
    </>
  )

  const uploadToS3 = async (file, contentType, presignedUrl) => {
    const fetchData = {
      method: 'PUT',
      headers: {
        'Content-Type': contentType,
      },
      body: file
    }

    try {
      const response = await fetch(presignedUrl, fetchData)
      return response
    }
    catch (e) {
      console.log(e)
    }
  }

  const onClear = async e => {
    setSelectedFiles([])
    setDropzoneKey(dropzoneKey + 1) // hack because dropzone doesn't expose a way to reset itself
  }

  const onSubmit = async (e, editedFile) => {
    e.preventDefault()
    
    if (validateFilenames(editedFile))
      return

    showLoading(true, "UploadFiles")
    setDisplayThumbnail()

    let submitAuction = 'aaa'
    let submitItem = 'iii'

    switch(props.imageType) {
        case "auction":
          submitAuction = auctionCode
          submitItem = 'banner'
          break;
        case "item":
          submitAuction = auctionCode
          submitItem = itemCode
          break;
        default:
          break;
    }

    const uploadResponses = []
    const uploadErrors = []
    for (const file of editedFile) {
      const presignedUrl = await adminGetAuctionImagePresignedUrl( submitAuction,  submitItem, file.name, file.type)
      console.log({presignedUrl})
      const response = await uploadToS3(file, file.type, presignedUrl)
      if (response && response.ok && response.status === 200) {
        uploadResponses.push(response)
        // set to always hide the new display thumbnail auction edit page will refresh when updated
        if (props.pageType === 'editAuction') setDisplayThumbnail()
        // otherwise set the display thumbnail
        else setDisplayThumbnail({auctionId: submitAuction, itemId: submitItem, fileName:file.name})
      }
      else {
        uploadErrors.push({ fileName: file.name, res: (response || {statusCode:'', statusText:'No response'}) })
      }
    }

    if (uploadErrors.length > 0) {
      setSubmitError(doUploadError(uploadErrors))
      // Function to clear image upload flag on upload error
      setSessionItem('imageUploadInProgress', null)
      showLoading(false)
      return
    }

    setDropzoneKey(dropzoneKey + 1) // hack because dropzone doesn't expose a way to reset itself

    if (props.imageType === 'auction') auctionUpdate()
    if (props.imageType ==='item') itemUpdate()

    setUploadDone(true)
    if (props.parentSubmitSuccess) props.parentSubmitSuccess(true)

    showLoading(false)
  }

  const doUploadError = (uploadErrors) => (
    <>
      <p>There was an error uploading some of your files</p>
      <ul>
        {uploadErrors.map((error, index) => (
          <li key={index}>{error.fileName}: {error.res.statusText} {error.res.status} </li>
        ))}
      </ul>
    </>
  )

  // For localhost we need to change domain to dev
  const getImgSrc = (displayThumbnail) => {
    let origin = window.location.origin

    if (origin === "http://localhost:3000")
      origin = "https://dev.auctions.support2.ucla.edu"

    return `${origin}/auctions/images/${displayThumbnail.auctionId}/${displayThumbnail.itemId}%7C${displayThumbnail.fileName}`
  }

  const doProcessError = (processResponse) => (
    <>
      <p>There was an error processing some of your files</p>

      {!processResponse.saveFileSuccess &&
        <div>Debug info: {JSON.stringify(processResponse,0,2)}</div>}

      {processResponse.saveFileError && processResponse.saveFileError.length > 0 &&
        <ul>
          {processResponse.saveFileError.map((error, index) => (
            <>
              {error.saveFileResponse && error.saveFileResponse.Error &&
                <li key={index}>File Save to DB Error: {error.saveFileResponse.Error.message}</li>}

              {error.processFileResponse && error.processFileResponse.Error &&
                <li key={index*-1}>File Process to S3 Error: {error.processFileResponse.Error.message}</li>}
            </>
          ))}
        </ul>}

      {processResponse.saveFileSuccess && (!processResponse.saveFileError || processResponse.saveFileError.length === 0) &&
        <div>File Save Debug Info: {JSON.stringify(processResponse.saveFileSuccess,0,2)}</div>}
    </>
  )

  if (isReadOnly) {
    return (
      <div className={css['readOnly-container']}>
        <p disabled={true} className={css['readOnly-text']}>Image upload is disabled in read-only mode.</p>
      </div>
    )
  }

  return (
    <div className={css.formStyles}>
      {isEditing && workingFile ? (
        <ImageEditor onSubmit={onSubmit} 
          isEditing={isEditing} setIsEditing={setIsEditing}
          workingFile={workingFile} setWorkingFile={setWorkingFile}
          imageType={props.imageType}
          />
    ) : (
    <>

     {selectedFiles.length === 0 && !uploadDone && <p>Files should be one of the following image types: .jpg, .jpeg, .png, .avif</p>}
     <p><b>
      Please upload images in a 16:9 ratio (e.g., 640x360 px) for best display.
      </b></p>

      {submitSuccess &&
        <Alert
          nonPromoPage
          icon={success}
          color={colors.guideGreen}
          width="20" height="15" top="40%" >
          <div>{submitSuccess}</div>
        </Alert>
      }

      {displayThumbnail &&
        <img alt="thumbnail" className={`${css['uploadedImage']} ${displayThumbnail.itemId === 'banner' ? themeCss['contain-image'] : ''}`} src={getImgSrc(displayThumbnail)} />
      }

      <form>
        <AuctionImageDropzone
          key={'dropzone-' + dropzoneKey}
          setSelectedFiles={setSelectedFiles}
        />

        {submitError &&
          <Alert icon={iconInfo} >
            <div>{submitError}</div>
          </Alert>
        }

        {/* <FilledYellow onClick={() => handleEdit(selectedFiles[0])} disabled={selectedFiles.length === 0}> Edit </FilledYellow>
        <FilledYellow type="submit" onClick={onSubmit} disabled={selectedFiles.length === 0 || submitError}> Upload </FilledYellow>
        <FilledYellow onClick={onClear} disabled={selectedFiles.length === 0}> Clear </FilledYellow> */}
      </form>
      </>
      )}
    </div>
  )
}

export default UploadFiles
